import { z } from 'zod';
import { allowsGrantingEdition, ItemPermWithEdit, itemEditPermSchema } from './item-edit-permission';
import { allowsGrantingView, ItemPermWithGrantView, itemGrantViewPermSchema } from './item-grant-view-permission';
import { itemViewPermSchema } from './item-view-permission';
import { allowsGrantingWatch, ItemPermWithWatch, itemWatchPermSchema } from './item-watch-permission';

export const itemOwnerPermSchema = z.object({
  isOwner: z.boolean()
});
export type ItemOwnerPerm = z.infer<typeof itemOwnerPermSchema>;

export const itemSessionPermSchema = z.object({
  canMakeSessionOfficial: z.boolean()
});
export type ItemSessionPerm = z.infer<typeof itemSessionPermSchema>;

export const itemEntryFromPermSchema = z.object({
  canEnterFrom: z.coerce.date(),
});
export const itemEntryUntilPermSchema = z.object({
  canEnterUntil: z.coerce.date(),
});
export const itemEntryTimePermSchema = itemEntryFromPermSchema.and(itemEntryUntilPermSchema);

export type ItemEntryTimePerm = z.infer<typeof itemEntryTimePermSchema>;

export const itemEnteringTimeIntervalsSchema = z.array(itemEntryTimePermSchema).optional().default([]);
export type ItemEnteringTimeIntervals = z.infer<typeof itemEnteringTimeIntervalsSchema>;

export const itemEnteringTimeIntervalsPermSchema = z.object({
  enteringTimeIntervals: itemEnteringTimeIntervalsSchema,
});
export type ItemEnteringTimeIntervalsPerm = z.infer<typeof itemEnteringTimeIntervalsPermSchema>;

export const itemCanRequestHelpSchema = z.object({
  canRequestHelp: z.boolean()
});

export const itemCorePermSchema = itemViewPermSchema
  .and(itemGrantViewPermSchema)
  .and(itemEditPermSchema)
  .and(itemWatchPermSchema)
  .and(itemOwnerPermSchema);

export type ItemCorePerm = z.infer<typeof itemCorePermSchema>;

/** Watched-group permissions as used in the item permissions panel (session flag filled locally). */
export type WatchedGroupPermissions = ItemCorePerm & ItemSessionPerm & ItemEnteringTimeIntervalsPerm;

export type ItemPermWithGive = ItemPermWithGrantView & ItemPermWithWatch & ItemPermWithEdit;
export interface ItemWithGivePerm { permissions: ItemPermWithGive }

/**
 * Whether the item permissions allows giving some permissions on the item to groups/users.
 * (warning: it requires also permissions on the receiving group/user!)
 */
function allowsGivingPermToItem(p: ItemPermWithGive): boolean {
  return allowsGrantingView(p) || allowsGrantingWatch(p) || allowsGrantingEdition(p);
}

export function canCurrentUserGivePermToItem(i: ItemWithGivePerm): boolean {
  return allowsGivingPermToItem(i.permissions);
}
